import OriginalCarImageIcon from "@/assets/illustrations/OriginalCarImageIcon.svg";
import i18n from "@/lang/i18n";
import EditCardValuesDialog from "./EditCardValuesDialog";
import { z } from "zod";
import { useDispatch } from "react-redux";
import { setOdometer, setSoc } from "@/redux/loginDetailsSlice";
import { useDriverSelfCheckIn } from "@/hooks";
import { canViewFeature } from "@/utils/appInitializer";
import { FeatureFlagNames } from "@/central-atoms/feature-flags/enums";

interface LoginTokenCarDetailsCardProps {
    vrn: string;
    odo: number;
    soc: number;
}

const formOdometerSchema = z.object({
  odometer: z.coerce.number().min(0, 'Odometer cannot be less than 0')
});

const formSocSchema = z.object({
  soc: z.coerce.number().min(0, 'Soc cannot be less than 0').max(100, 'Soc cannot be more than 100')
});

const LoginTokenCarDetailsCard: React.FC<LoginTokenCarDetailsCardProps> = ({ vrn, odo, soc }) => {
  const dispatch = useDispatch();

  const handleSocEdit = (data: z.infer<typeof formSocSchema>) => {
    dispatch(setSoc(data.soc));
  }

  const handleOdometerEdit = (data: z.infer<typeof formOdometerSchema>) => {
    dispatch(setOdometer(data.odometer));
  }

  const isEditDisabled = canViewFeature(FeatureFlagNames.should_disable_odo_and_soc_edit);

  return (
    <div className="shadow-[0px_2px_0px_#3ebec8] rounded-[30px] box-border border-2 border-solid border-primary flex justify-center items-center pt-4 bg-white">
      <div className="flex flex-col items-center w-full">
        <h5 className="text-secondary text-[20px] font-semibold mb-3">{i18n.t('driver_check_in')}</h5>
        <img src={OriginalCarImageIcon} className="w-60" />
        <div className="">
          <div className="flex flex-col items-center">
            <h5 className="text-secondary font-semibold text-[20px] leading-[40px]">{vrn}</h5>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 w-full border-primary border-t-[1px]">
          <div className="flex flex-col justify-center ml-5 pr-[23px] border-primary border-r-[1px] pt-4 pb-2">
            <span>
              <p className="text-secondary text-[14px] inline mr-[4px]">{i18n.t('odometer')}</p>
              <EditCardValuesDialog disabled={isEditDisabled} handleSubmit={handleOdometerEdit} placeholder={i18n.t('enter_odometer')} formSchema={formOdometerSchema} fieldType="number" fieldName="odometer" defaultValue={odo.toString()} title={i18n.t('odometer')} inputPrependText={i18n.t('km')} />
            </span>
            <h5 className="text-primary font-semibold text-[20px]">{odo.toFixed(0)} {i18n.t('km')}</h5>
          </div>
          <div className="flex flex-col items-center justify-center mr-5 pt-4 pb-2">
            <span>
              <p className="text-secondary text-[14px] inline mr-[4px]">{i18n.t('soc')}</p>
              <EditCardValuesDialog disabled={isEditDisabled} handleSubmit={handleSocEdit} placeholder={i18n.t('soc')} formSchema={formSocSchema} fieldType="number" fieldName="soc" defaultValue={soc.toString()} title={i18n.t('soc')} inputPrependText="%" />
            </span>
            <h5 className="text-primary font-semibold text-[20px]">{soc}%</h5>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginTokenCarDetailsCard;
